import React, { useCallback, useRef } from "react";
import { Button, Modal, Spin } from "antd";
import { UploadOutlined, CameraOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";

/**
 * Row-triggered petrifilm upload activity (choose file / take photo / preview / submit).
 * When `petrifilmImageMandatory`, close only via Submit once an image exists (no X / mask / Escape).
 * When optional, user may Skip, use X, mask click, or Escape via `onSkip`.
 */
export function PetrifilmUploadActivityModal({
  open,
  onSubmit,
  onSkip,
  petrifilmImageMandatory = true,
  submitButtonText = "Submit",
  uploadModalCommitLoading,
  petrifilmPreviewLoading = false,
  /** False until user has chosen an image, a preview exists, or a stored key is present (parent decides). */
  canSubmit = false,
  helpText,
  fileInputRef,
  canUploadImage,
  displayImageUrl,
  previewLabelText,
  fileName,
  localPreviewUrl,
  existingPetrifilmImageUrlFromServer,
  onTakePhoto,
  onOpenFullPreview,
}) {
  const actionsLocked = uploadModalCommitLoading || petrifilmPreviewLoading;
  /** Thumbnail can open full size once `displayImageUrl` exists (e.g. local file while server preview still loads). */
  const previewThumbLocked = uploadModalCommitLoading || (petrifilmPreviewLoading && !displayImageUrl);
  const showSubmitButton = canSubmit || uploadModalCommitLoading;

  let footer = null;
  if (petrifilmImageMandatory) {
    if (showSubmitButton) {
      footer = (
        <Button
          type="primary"
          loading={uploadModalCommitLoading}
          onClick={onSubmit}
        >
          {submitButtonText}
        </Button>
      );
    }
  } else {
    footer = (
      <>
        <Button
          key="close"
          disabled={uploadModalCommitLoading}
          onClick={() => {
            if (onSkip) {
              onSkip();
            }
          }}
        >
          Close
        </Button>
        {showSubmitButton && (
          <Button
            key="submit"
            type="primary"
            loading={uploadModalCommitLoading}
            onClick={onSubmit}
          >
            {submitButtonText}
          </Button>
        )}
      </>
    );
  }

  return (
    <Modal
      title="Petrifilm image upload"
      open={open}
      onCancel={() => {
        if (!petrifilmImageMandatory && onSkip) {
          onSkip();
        }
      }}
      closable={!petrifilmImageMandatory}
      maskClosable={!petrifilmImageMandatory}
      keyboard={!petrifilmImageMandatory}
      footer={footer}
      destroyOnClose
      width={480}
      centered
      className="PetrifilmImageSection__UploadActivityModal"
    >
      <Spin
        spinning={actionsLocked}
        tip={uploadModalCommitLoading ? "Saving…" : "Loading image…"}
        className="PetrifilmImageSection__UploadActivitySpin"
      >
        <div className="PetrifilmImageSection__UploadActivityBody">
          <div className={`PetrifilmImageSection__Help${petrifilmImageMandatory ? " PetrifilmImageSection__Help--mandatory" : ""}`}>
            {petrifilmImageMandatory && (
              <span title="Required" className="PetrifilmImageSection__RequiredStar">
                *
              </span>
            )}
            <span>{helpText}</span>
          </div>
          <div className="PetrifilmImageSection__Actions PetrifilmImageSection__Actions--uploadModal">
            <Button
              type="default"
              icon={<UploadOutlined />}
              disabled={!canUploadImage || actionsLocked}
              onClick={() => {
                const input = fileInputRef.current;
                if (input) input.click();
              }}
            >
              Choose image
            </Button>
            <Button
              type="default"
              icon={<CameraOutlined />}
              disabled={!canUploadImage || actionsLocked}
              onClick={onTakePhoto}
            >
              Take photo
            </Button>
          </div>
          {!fileName && existingPetrifilmImageUrlFromServer && !localPreviewUrl && (
            <span className="PetrifilmImageSection__SourceNote PetrifilmImageSection__SourceNote--uploadModal">
              Replace with Choose / Take photo
            </span>
          )}
          {displayImageUrl && (
            <div className="PetrifilmImageSection__UploadModalPreview">
              <button
                type="button"
                className="PetrifilmImageSection__UploadModalPreviewTrigger"
                onClick={onOpenFullPreview}
                disabled={previewThumbLocked}
                title="View full size"
              >
                <img
                  src={displayImageUrl}
                  alt={previewLabelText ? `Petrifilm preview: ${previewLabelText}` : "Petrifilm preview"}
                  className="PetrifilmImageSection__UploadModalPreviewImg"
                />
              </button>
              {previewLabelText && (
                <div className="PetrifilmImageSection__UploadModalPreviewCaption">
                  {previewLabelText}
                </div>
              )}
            </div>
          )}
        </div>
      </Spin>
    </Modal>
  );
}

/**
 * Desktop webcam: owns MediaStream and video element; parent only toggles open and receives the captured File.
 */
export function PetrifilmCameraModal({
  open,
  onClose,
  onCaptureSuccess,
  onWebcamUnavailable,
}) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const stopWebcamStream = useCallback(() => {
    const stream = streamRef.current;
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }
    streamRef.current = null;
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  const attachStreamToVideo = useCallback(() => {
    const video = videoRef.current;
    const stream = streamRef.current;
    if (!video || !stream) {
      return false;
    }
    video.srcObject = stream;
    video.play().catch(() => undefined);
    return true;
  }, []);

  const handleAfterOpenChange = useCallback((isOpen) => {
    if (!isOpen) {
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
      stopWebcamStream();
      return;
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      if (onWebcamUnavailable) {
        onWebcamUnavailable();
      }
      onClose();
      return;
    }

    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" } },
          audio: false,
        });
        streamRef.current = stream;
        if (!attachStreamToVideo()) {
          requestAnimationFrame(() => {
            if (!attachStreamToVideo()) {
              toast.error("Could not start camera preview.");
              onClose();
              stopWebcamStream();
            }
          });
        }
      } catch {
        toast.warning("Webcam unavailable. Try Choose image or check browser permissions.");
        if (onWebcamUnavailable) {
          onWebcamUnavailable();
        }
        onClose();
      }
    })();
  }, [attachStreamToVideo, onClose, onWebcamUnavailable, stopWebcamStream]);

  const handleCapture = useCallback(() => {
    const video = videoRef.current;
    if (!video || video.readyState < 2 || video.videoWidth === 0) {
      toast.error("Camera not ready. Wait a moment or try again.");
      return;
    }
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext("2d");
    context.drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      if (!blob) {
        toast.error("Could not capture image.");
        return;
      }
      const file = new File([blob], `petrifilm-${Date.now()}.jpg`, { type: "image/jpeg" });
      onCaptureSuccess(file);
      onClose();
    }, "image/jpeg", 0.92);
  }, [onCaptureSuccess, onClose]);

  return (
    <Modal
      title="Take petrifilm photo"
      open={open}
      onCancel={onClose}
      afterOpenChange={handleAfterOpenChange}
      destroyOnClose
      width={560}
      footer={(
        <>
          <Button onClick={onClose}>
            Cancel
          </Button>
          <Button type="primary" onClick={handleCapture}>
            Capture
          </Button>
        </>
      )}
      className="PetrifilmImageSection__CameraModal"
    >
      <div className="PetrifilmImageSection__CameraBody">
        <video
          ref={videoRef}
          className="PetrifilmImageSection__CameraVideo"
          muted
          playsInline
          aria-label="Camera preview"
        />
      </div>
    </Modal>
  );
}

/** Full-size image viewer (main card and upload modal). */
export function PetrifilmImagePreviewModal({
  open,
  onCancel,
  displayImageUrl,
}) {
  return (
    <Modal
      title="Petrifilm image"
      open={open}
      onCancel={onCancel}
      footer={null}
      width={720}
      centered
      destroyOnClose
      className="PetrifilmImageSection__ImagePreviewModal"
    >
      {displayImageUrl && (
        <img
          className="PetrifilmImageSection__ImagePreviewImg"
          src={displayImageUrl}
          alt="Petrifilm"
        />
      )}
    </Modal>
  );
}
